import { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import toast from "react-hot-toast";
import Cookies from "js-cookie";

const NOTE_MAX_LENGTH = 5000;

const ClockIcon = () => (
    <svg
        className="h-4 w-4 shrink-0 text-gray-400"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 6v6l4 2" />
    </svg>
);

const TrashIcon = () => (
    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const CheckIcon = () => (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
        <path d="M5 12l4 4 10-10" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

export const NotesPage = ({ isVoiceModalOpen, setIsVoiceModalOpen }) => {
    const token = Cookies.get("auth_token");

    const [note, setNote] = useState("");
    const [voiceNote, setVoiceNote] = useState(null);
    const [activeTab, setActiveTab] = useState(note && note.trim() ? "note" : "voice");
    const [updatedAt, setUpdatedAt] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [deleteModal, setDeleteModal] = useState({ open: false, deleteType: null });

    const audioRef = useRef(null);
    const textareaRef = useRef(null);
    const savedNoteRef = useRef("");

    /* ================= FETCH ================= */
    const fetchProfileNote = async () => {
        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/profile-note/get`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );
            const data = await res.json();

            if (data.status === "1" && data.data?.length > 0) {
                const d = data.data[0];
                setNote(d.profile_note || "");
                setVoiceNote(d.profile_note_audio || null);
                setUpdatedAt(d.profile_note_updated_at || null);
            } else {
                setNote("");
                setVoiceNote(null);
                setUpdatedAt(null);
            }
        } catch {
            toast.error("Unable to load note");
        }
    };

    useEffect(() => {
        fetchProfileNote();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        fetchProfileNote();
        // eslint-disable-next-line
    }, [isVoiceModalOpen]);



    /* ================= SAVE ================= */
    const handleSave = async () => {
        if (!note.trim()) {
            toast.error("Note is empty");
            return;
        }

        try {
            const formData = new FormData();
            formData.append("note", note.trim());
            formData.append("voice_note", new Blob(), "");

            const res = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/profile-note/set`,
                {
                    method: "POST",
                    headers: { Authorization: `Bearer ${token}` },
                    body: formData,
                }
            );
            const data = await res.json();

            if (data.status === "1") {
                toast.success("Note saved");
                setIsEditing(false);
                fetchProfileNote();
            } else {
                toast.error("Failed to save note");
            }
        } catch {
            toast.error("Error saving note");
        }
    };

    /* ================= AUDIO ================= */
    const audioUrl =
        voiceNote?.url ||
        (typeof voiceNote === "string" ? voiceNote : null);

    const resolvedAudioSrc = audioUrl
        ? audioUrl.startsWith("http")
            ? audioUrl
            : `${process.env.REACT_APP_API_BASE_URL}${audioUrl}`
        : null;

    const handlePlayPause = () => {
        if (!audioRef.current) return;

        if (isPlaying) {
            audioRef.current.pause();
        } else {
            audioRef.current.play();
        }
        setIsPlaying((p) => !p);
    };

    const openDeleteModal = (deleteType) => {
        setDeleteModal({ open: true, deleteType });
    };

    const closeDeleteModal = () => {
        setDeleteModal({ open: false, deleteType: null });
    };

    const confirmDelete = async () => {
        const { deleteType } = deleteModal;
        closeDeleteModal();
        if (!deleteType) return;

        try {
            const formData = new FormData();
            formData.append("delete_type", deleteType);

            const res = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/profile-note/delete`,
                {
                    method: "POST",
                    headers: { Authorization: `Bearer ${token}` },
                    body: formData,
                }
            );
            const data = await res.json();

            if (data.status === "1") {
                toast.success(data.message || "Deleted successfully");
                if (deleteType === "voice_note") {
                    setVoiceNote(null);
                    setIsPlaying(false);
                    if (audioRef.current) {
                        audioRef.current.pause();
                    }
                } else {
                    setNote("");
                    setIsEditing(false);
                }
                fetchProfileNote();
            } else {
                toast.error(data.message || "Failed to delete");
            }
        } catch {
            toast.error("Error deleting");
        }
    };

    const deleteModalCopy =
        deleteModal.deleteType === "voice_note"
            ? {
                  title: "Delete Voice Note?",
                  message:
                      "Are you sure you want to delete this voice note? This action cannot be undone.",
              }
            : {
                  title: "Delete Note?",
                  message:
                      "Are you sure you want to delete this note? This action cannot be undone.",
              };

    const handleAudioEnded = () => {
        setIsPlaying(false);
        if (audioRef.current) {
            audioRef.current.currentTime = 0;
        }
    };

    useEffect(() => {
        if (resolvedAudioSrc && audioRef.current) {
            audioRef.current.load();
            setIsPlaying(false);
        }
    }, [resolvedAudioSrc]);

    /* ================= HELPERS ================= */
    const openVoiceModal = () => {
        setIsVoiceModalOpen?.(true);
    };

    const formatDateTime = (d) =>
        d
            ? new Date(d).toLocaleString("en-IN", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
              })
            : "";

    const startEditing = () => {
        savedNoteRef.current = note;
        setIsEditing(true);
    };

    const cancelEditing = () => {
        setNote(savedNoteRef.current);
        setIsEditing(false);
    };

    useEffect(() => {
        if (isEditing) textareaRef.current?.focus();
    }, [isEditing]);

    /* ================= UI ================= */
    return (
        <>
        <div className="flex min-h-full flex-col rounded-2xl bg-white border border-gray-100 p-5 shadow-sm md:p-6">
            <div className="mb-4 flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Notes</h2>
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={() => {
                            setActiveTab("note");
                            setIsEditing(false);
                        }}
                        className={`rounded-2xl bg-white px-4 py-1 text-sm font-medium shadow-sm transition ${
                            activeTab === "note"
                                ? "border border-[#7544FC] text-[#7544FC]"
                                : "border border-transparent text-gray-600"
                        }`}
                    >
                        Note
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            setActiveTab("voice");
                            setIsEditing(false);
                        }}
                        className={`rounded-2xl bg-white px-4 py-1 text-sm font-medium shadow-sm transition ${
                            activeTab === "voice"
                                ? "border border-[#7544FC] text-[#7544FC]"
                                : "border border-transparent text-gray-600"
                        }`}
                    >
                        Voice
                    </button>
                </div>
            </div>

            {activeTab === "note" && (
                <div className="mb-3 flex min-h-[36px] items-center justify-between gap-3">
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                        {updatedAt && (
                            <>
                                <ClockIcon />
                                <span>Last updated: {formatDateTime(updatedAt)}</span>
                            </>
                        )}
                    </div>
                    {!isEditing && (
                        <button
                            type="button"
                            onClick={startEditing}
                            className="inline-flex items-center justify-center rounded-xl border border-[#7A5AF8] bg-white px-4 py-2 text-sm font-semibold text-[#7A5AF8] transition hover:bg-[#F5F3FF]"
                        >
                            Edit Note
                        </button>
                    )}
                </div>
            )}

            {/* ================= NOTE TAB ================= */}
            {activeTab === "note" && (
                <>
                    <div className="relative">
                        <textarea
                            ref={textareaRef}
                            value={note}
                            readOnly={!isEditing}
                            maxLength={NOTE_MAX_LENGTH}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder={
                                isEditing
                                    ? "Write your note..."
                                    : "Tap Edit Note to add or modify your note"
                            }
                            className={`h-64 w-full resize-none rounded-2xl border px-4 py-3 pb-10 text-sm leading-6 outline-none transition ${
                                isEditing
                                    ? "border-[#C4B5FD] bg-white text-gray-800 placeholder:text-gray-400 focus:border-[#7A5AF8] focus:ring-2 focus:ring-[#7A5AF8]/15"
                                    : "cursor-default border-gray-200 bg-gray-50 text-gray-700 placeholder:text-gray-400"
                            }`}
                        />
                        {isEditing && (
                            <span className="pointer-events-none absolute bottom-3 right-4 text-xs text-gray-400">
                                {note.length}/{NOTE_MAX_LENGTH}
                            </span>
                        )}
                    </div>

                    <div className="mt-4 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                            <button
                                type="button"
                                onClick={() => openDeleteModal("note")}
                                disabled={!note.trim() || isEditing}
                                className="inline-flex items-center justify-center gap-2 rounded-xl border border-red-400 bg-white px-5 py-2.5 text-sm font-semibold text-red-500 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-300"
                            >
                                <TrashIcon />
                                Delete Note
                            </button>
                            {isEditing && (
                                <button
                                    type="button"
                                    onClick={cancelEditing}
                                    className="inline-flex items-center justify-center rounded-xl px-5 py-2.5 text-sm font-semibold text-gray-600 transition hover:bg-gray-100"
                                >
                                    Cancel
                                </button>
                            )}
                        </div>
                        {isEditing && (
                            <button
                                type="button"
                                onClick={handleSave}
                                className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#6C45FF] to-[#5D7BFF] px-6 py-2.5 text-sm font-semibold text-white shadow-[0_8px_24px_rgba(108,69,255,0.28)] transition hover:brightness-105"
                            >
                                <CheckIcon />
                                Save Note
                            </button>
                        )}
                    </div>
                </>
            )}

            {/* ================= VOICE TAB ================= */}
            {activeTab === "voice" && (
                <div className="mt-8 flex justify-center">
                    <div className="relative w-full max-w-md rounded-3xl border border-[#7A5AF8] bg-white p-8 flex flex-col items-center text-center shadow-sm">
                        {resolvedAudioSrc && (
                            <button
                                type="button"
                                onClick={() => openDeleteModal("voice_note")}
                                aria-label="Delete voice note"
                                className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full text-red-500 transition hover:bg-red-50"
                            >
                                <TrashIcon />
                            </button>
                        )}
                        <div
                            className={`w-20 h-24 rounded-2xl flex flex-col items-center justify-center mb-5 transition ${
                                resolvedAudioSrc 
                                    ? "bg-[#7A5AF8]"
                                    : "bg-[#F1EDFF]"
                            }`}
                        >
                            <img
                                  src={
                                 isPlaying
                                        ? "../chatscreen/pause.svg"
                                        : resolvedAudioSrc ? "../chatscreen/volume.svg" : "../chatscreen/speaker.svg"
                                }
                                className="w-8 h-8"
                                alt="voice"
                            />
                            <img
                                src={resolvedAudioSrc ? "../chatscreen/wave-form.svg" : "../chatscreen/audio-wave.svg"}
                                className="w-10 h-10"
                                alt="waveform"
                            />
                        </div>

                        <h3 className="text-lg font-semibold mt-6">
                            {resolvedAudioSrc
                                ? "Voice Note Added"
                                : "No Voice Note Yet"}
                        </h3>

                        <p className="text-sm text-gray-500 mt-2 mb-8">
                            {resolvedAudioSrc
                                ? "Tap play to listen to your voice note."
                                : "Record a quick voice note to remember things faster."}
                        </p>

                        {resolvedAudioSrc ? (
                            <button
                                onClick={handlePlayPause}
                                className="w-full rounded-full py-3 submit-button"
                            >
                                {isPlaying
                                    ? "Pause Voice Note"
                                    : "Play Voice Note"}
                            </button>
                        ) : (
                            <button
                                onClick={openVoiceModal}
                                className="w-full rounded-full py-3 submit-button"
                            >
                                Record Voice Note
                            </button>
                        )}

                        {resolvedAudioSrc && (
                            <button
                                onClick={openVoiceModal}
                                className="mt-3 text-sm text-[#7A5AF8] font-medium hover:underline"
                            >
                                Edit Voice Note
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* AUDIO ELEMENT */}
            {resolvedAudioSrc && (
                <audio
                    ref={audioRef}
                    src={resolvedAudioSrc}
                    onEnded={handleAudioEnded}
                />
            )}
        </div>

        {deleteModal.open &&
            ReactDOM.createPortal(
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
                    <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-sm text-center">
                        <div className="mb-4 flex justify-center flex-col items-center">
                            <div className="h-14 w-14 flex mb-4 justify-center items-center bg-[linear-gradient(180deg,#F87171_0%,#EF4444_29%,#B91C1C_100%)] rounded-full shadow-sm">
                                <svg
                                    fill="#ffffff"
                                    viewBox="0 0 24 24"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="w-7 h-7"
                                >
                                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                                </svg>
                            </div>
                            <h3 className="text-lg font-bold text-[#6938EF] mb-2">
                                {deleteModalCopy.title}
                            </h3>
                            <p className="text-gray-600 text-sm">
                                {deleteModalCopy.message}
                            </p>
                        </div>
                        <div className="flex gap-4 justify-center mt-6">
                            <button
                                type="button"
                                className="px-6 py-2 rounded-full bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200 transition"
                                onClick={closeDeleteModal}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="px-6 py-2 rounded-full delete-button font-semibold"
                                onClick={confirmDelete}
                            >
                                Delete 
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

